import React from "react";

import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { IconProps } from "@mui/material";
import { Hierarchy } from "assets/material-custom-icons";
import { DealRedirect } from "constants/deal";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import {
  useAccountSelector,
  useDealIdSelector,
  useDealStatusSelector,
  useDealTypeSelect,
  useOpportunityIdSelector,
  usePickUserPermissions,
  useUserRoleSelector,
  useViewActions
} from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { AsyncContentMui } from "../../Async-content/AsyncContentMui";
import { HorizontalNavBar } from "../../HorizontalNavBar/HorizontalNavBar";

// import { DealRedirect } from "../../../constants/deal";

const iconStyles: IconProps["sx"] = {
  fontSize: "18px"
};

const databoxAreaRoutes: string[] = [
  Routes.dealWorkspace,
  Routes.dealFolder,
  Routes.dealForm,
  Routes.dealFile,
  Routes.dealEmails,
  Routes.dealNews
];

export const NavBarHorizontal: React.FC = () => {
  const id = useDealIdSelector();
  const status = useDealStatusSelector();
  const userRole = useUserRoleSelector();
  const navigate = useNavigate();
  const { look_through_investments, show_debt } = useAccountSelector() || {};
  const { setDealRedirectAction } = useViewActions();
  const portfolioAnalysis = usePickUserPermissions("portfolio_analysis");
  const taskManager = usePickUserPermissions("task_manager");
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const isDebt = useDealTypeSelect() === "debt";
  const noAccessPermissionReport = portfolioAnalysis && portfolioAnalysis === UserPermissionType.NO_ACCESS;
  const noAccessPermissionTask = taskManager && taskManager === UserPermissionType.NO_ACCESS;
  const noAccessPermissionMetrics = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;

  const opportunityId = useOpportunityIdSelector();

  const isTabsAvailable = status?.id !== opportunityId;

  return (
    <AsyncContentMui isLoading={!status} spinnerProps={{ size: 24 }}>
      {isTabsAvailable ? (
        <HorizontalNavBar
          tabs={[
            {
              icon: <GridViewOutlinedIcon sx={iconStyles} />,
              label: "Overview",
              value: "Overview",
              matchRouts: [Routes.dealDashboard],
              onClick: () => id && navigate({ route: "dealDashboard", params: { deal_id: id } })
            },
            {
              icon: <FolderOpenOutlinedIcon sx={iconStyles} />,
              label: "Workspace",
              value: "Workspace",
              matchRouts: databoxAreaRoutes,
              onClick: () => setDealRedirectAction(DealRedirect.Workspace)
            },
            {
              icon: <InsertChartOutlinedIcon sx={iconStyles} />,
              label: "Cashflow and Data",
              value: "Cashflow and Data",
              matchRouts: [Routes.dealMonitoring],
              onClick: () => id && navigate({ route: "dealMonitoringData", params: { deal_id: id } }),
              hidden: noAccessPermissionMetrics
            },
            {
              icon: <AutoGraphIcon sx={iconStyles} />,
              label: "Report",
              value: "Report",
              matchRouts: [Routes.dealReport],
              onClick: () => id && navigate({ route: "dealReport", params: { deal_id: id } }),
              hidden: noAccessPermissionReport,
              disabled: status && status.name !== "Closed" && status.name !== "Invested"
            },
            {
              icon: <TaskAltOutlinedIcon sx={iconStyles} />,
              label: "Task Manager",
              value: "Task Manager",
              matchRouts: [Routes.dealTaskManage],
              hidden: noAccessPermissionTask || userRole?.presentation_name === RoleName.Investor,
              onClick: () => id && navigate({ route: "dealTaskManage", params: { deal_id: id } })
            },
            {
              icon: <Hierarchy sx={{ ...iconStyles, fill: "#5d7782" }} />,
              label: "Look Through",
              value: "Look Through",
              matchRouts: [Routes.dealLookThrough],
              hidden: !look_through_investments,
              onClick: () => id && navigate({ route: "dealLookThrough", params: { deal_id: id } })
            },
            {
              icon: <ViewWeekOutlinedIcon sx={iconStyles} />,
              label: "Amortization Table",
              value: "Amortization Table",
              matchRouts: [Routes.dealAmortization],
              hidden: !isDebt || !show_debt,
              onClick: () => id && navigate({ route: "dealAmortization", params: { deal_id: id } })
            }
          ]}
        />
      ) : (
        <HorizontalNavBar
          tabs={[
            {
              icon: <FolderOpenOutlinedIcon sx={iconStyles} />,
              label: "Workspace",
              value: "Workspace"
            }
          ]}
        />
      )}
    </AsyncContentMui>
  );
};
